import React, { useState, useEffect } from 'react';
import { Clock, CheckCircle, AlertCircle, Loader, RefreshCw } from 'lucide-react';
import processingQueue from '../utils/processingQueue';

const ProcessingQueueStatus = () => {
  const [queueStatus, setQueueStatus] = useState({
    queueLength: 0,
    processing: false,
    currentItem: null,
    queueItems: [],
    recentlyCompleted: null
  });

  useEffect(() => {
    // Immediately check status on mount
    const status = processingQueue.getStatus();
    setQueueStatus(status);
    
    // Update queue status frequently (300ms) to catch recently completed items fast
    const interval = setInterval(() => {
      const status = processingQueue.getStatus();
      setQueueStatus(status);
      
      // Check for stuck items every 30 seconds
      processingQueue.checkForStuckItems();
    }, 300);

    return () => clearInterval(interval);
  }, []);

  // Show toast if: queue has items, is processing, OR recently completed something
  const hasRecentlyCompleted = queueStatus.recentlyCompleted != null;
  
  // Debug logging
  if (hasRecentlyCompleted) {
    console.log('🔔 [Toast] Showing recently completed:', queueStatus.recentlyCompleted);
  }
  
  if (queueStatus.queueLength === 0 && !queueStatus.processing && !hasRecentlyCompleted) {
    return null;
  }

  return (
    <div className="fixed bottom-4 right-4 bg-white rounded-lg shadow-lg border border-gray-200 p-4 max-w-sm z-50">
      <div className="flex items-center space-x-2 mb-2">
        <Clock size={16} className="text-blue-600" />
        <span className="text-sm font-medium text-gray-900">Processing Queue</span>
      </div>
      
      {/* Show recently completed toast for images */}
      {hasRecentlyCompleted && !queueStatus.processing && queueStatus.queueLength === 0 && (
        <div className="flex items-center space-x-2 mb-2 p-2 bg-green-50 rounded">
          <CheckCircle size={14} className="text-green-600" />
          <span className="text-xs text-green-800">
            {queueStatus.recentlyCompleted.type === 'image' 
              ? `Queued "${queueStatus.recentlyCompleted.displayName}" for AI analysis`
              : `Completed: ${queueStatus.recentlyCompleted.displayName}`
            }
          </span>
        </div>
      )}
      
      {queueStatus.processing && queueStatus.currentItem && (
        <div className="flex items-center space-x-2 mb-2 p-2 bg-blue-50 rounded">
          <Loader size={14} className="text-blue-600 animate-spin" />
          <span className="text-xs text-blue-800">
            Processing: {queueStatus.currentItem}
          </span>
        </div>
      )}
      
      {queueStatus.queueLength > 0 && (
        <div className="space-y-1">
          <div className="text-xs text-gray-600 mb-1">
            {queueStatus.queueLength} item{queueStatus.queueLength !== 1 ? 's' : ''} waiting
          </div>
          {queueStatus.queueItems.slice(0, 3).map((item, index) => (
            <div key={item.id} className="flex items-center space-x-2 text-xs text-gray-600">
              <div className="w-2 h-2 bg-gray-300 rounded-full"></div>
              <span>{item.displayName}</span>
              <span className="text-gray-400">
                ({Math.round(item.waitTime / 1000)}s)
              </span>
            </div>
          ))}
          {queueStatus.queueLength > 3 && (
            <div className="text-xs text-gray-400">
              +{queueStatus.queueLength - 3} more...
            </div>
          )}
        </div>
      )}
      
      {/* Only show queue footer when actually processing or has items waiting */}
      {(queueStatus.processing || queueStatus.queueLength > 0) && (
        <div className="flex items-center justify-between">
          <div className="text-xs text-gray-500">
            Items are processed one at a time to ensure quality
          </div>
          <button
            onClick={() => {
              console.log('🔄 Manual queue recovery triggered');
              processingQueue.forceRecovery();
            }}
            className="text-xs text-blue-600 hover:text-blue-800 flex items-center space-x-1"
            title="Force recovery if queue appears stuck"
          >
            <RefreshCw size={12} />
            <span>Try Again</span>
          </button>
        </div>
      )}
    </div>
  );
};

export default ProcessingQueueStatus;
